import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ModalWrapper } from '../modals/ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';
import { communicationService, Communication } from '../../services/communicationService';
import { patientService, Patient } from '../../services/patientService';
import { leadService, Lead } from '../../services/leadService';
import { getApiError } from '../../utils/apiError';

interface Props {
  open: boolean;
  onClose: () => void;
  comm: Communication | null;
  tenantId: number;
  onAssigned: () => void;
}

const AssignCommunicationDialog: React.FC<Props> = ({
  open, onClose, comm, tenantId, onAssigned,
}) => {
  // Which list is being searched. An unmatched message is often from somebody
  // who wrote in before they were taken on - a lead - and filing it used to
  // mean inventing a patient record for them first.
  const [target, setTarget] = useState<'patient' | 'lead'>('patient');
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Patient[]>([]);
  const [leadResults, setLeadResults] = useState<Lead[]>([]);
  const [selected, setSelected] = useState<Patient | null>(null);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [searching, setSearching] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset when (re)opened.
  useEffect(() => {
    if (open) {
      setTarget('patient');
      setSearch('');
      setResults([]);
      setLeadResults([]);
      setSelected(null);
      setSelectedLead(null);
    }
  }, [open, comm?.id]);

  // Debounced patient search.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const term = search.trim();
    if (term.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        if (target === 'lead') {
          const res = await leadService.getLeads({ search: term, limit: 10 });
          setLeadResults(res.items);
        } else {
          const res = await patientService.getPatients(
            tenantId, [], [], 0, 10, undefined, undefined, term,
          );
          setResults(res.items);
        }
      } catch (err) {
        toast.error(getApiError(err, `Failed to search ${target}s`));
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, tenantId, target]);

  const handleAssign = async () => {
    if (!comm) return;
    const name = target === 'lead'
      ? (selectedLead && `${selectedLead.first_name} ${selectedLead.last_name}`.trim())
      : selected?.full_name;
    if (!name) return;
    try {
      setAssigning(true);
      if (target === 'lead' && selectedLead) {
        await communicationService.assignToLead(comm.id, selectedLead.id);
      } else if (selected) {
        await communicationService.assignToPatient(comm.id, selected.id);
      }
      toast.success(`Assigned to ${name}`);
      onAssigned();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to assign message'));
    } finally {
      setAssigning(false);
    }
  };

  const hasSelection = target === 'lead' ? !!selectedLead : !!selected;

  if (!comm) return null;

  return (
    <ModalWrapper
      isOpen={open}
      onClose={onClose}
      title="Assign message"
      isLoading={assigning}
      width="max-w-lg"
      titleAlign="left"
      titleClassName="text-lg font-bold text-[#366A75] leading-tight"
    >
      <div className="space-y-4">
        <div className="space-y-1 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-xs">
          <div className="flex gap-2">
            <span className="text-gray-500 w-16 shrink-0">From</span>
            <span className="text-gray-800 break-all">{comm.from_address || '-'}</span>
          </div>
          <div className="flex gap-2">
            <span className="text-gray-500 w-16 shrink-0">Subject</span>
            <span className="text-gray-800 break-words">{comm.subject || '(no subject)'}</span>
          </div>
        </div>

        <div className="flex gap-2">
          {(['patient', 'lead'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => {
                setTarget(t);
                setSearch('');
                setResults([]);
                setLeadResults([]);
                setSelected(null);
                setSelectedLead(null);
              }}
              disabled={assigning}
              className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                target === t
                  ? 'bg-[#366A75] text-white border-[#366A75]'
                  : 'bg-white text-[#366A75] border-[#B4D9D4] hover:bg-[#EBF5F5]'
              }`}
            >
              {t === 'patient' ? 'Patient' : 'Lead'}
            </button>
          ))}
        </div>

        <div>
          <label className="block text-[#366A75] font-medium mb-2">
            Search {target}
          </label>
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setSelected(null); }}
            placeholder="Name, email or phone…"
            disabled={assigning}
            className={POPUP_STYLES.input}
            autoFocus
          />
        </div>

        <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
          {searching ? (
            <div className="text-center text-sm text-gray-500 py-6">Searching…</div>
          ) : search.trim().length < 2 ? (
            <div className="text-center text-sm text-gray-400 py-6">Type at least 2 characters</div>
          ) : target === 'lead' ? (
            leadResults.length === 0 ? (
              <div className="text-center text-sm text-gray-500 py-6">No matching leads</div>
            ) : (
              leadResults.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => setSelectedLead(l)}
                  className={`w-full text-left px-3 py-2 transition-colors ${
                    selectedLead?.id === l.id ? 'bg-[#D0E7E5]' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="text-sm font-medium text-gray-900 break-words">
                    {`${l.first_name} ${l.last_name}`.trim()}
                  </div>
                  <div className="text-xs text-gray-500 break-all">
                    {[l.email, l.phone].filter(Boolean).join('  ·  ') || `Lead #${l.id}`}
                  </div>
                </button>
              ))
            )
          ) : results.length === 0 ? (
            <div className="text-center text-sm text-gray-500 py-6">No matching patients</div>
          ) : (
            results.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setSelected(p)}
                className={`w-full text-left px-3 py-2 transition-colors ${
                  selected?.id === p.id ? 'bg-[#D0E7E5]' : 'hover:bg-gray-50'
                }`}
              >
                <div className="text-sm font-medium text-gray-900 break-words">{p.full_name}</div>
                <div className="text-xs text-gray-500 break-all">
                  {[p.email, p.phone].filter(Boolean).join('  ·  ') || `Patient #${p.id}`}
                </div>
              </button>
            ))
          )}
        </div>

        <div className="flex gap-4 justify-center pt-2">
          <button
            onClick={handleAssign}
            disabled={assigning || !hasSelection}
            className={POPUP_STYLES.button}
          >
            {assigning ? 'Assigning…' : 'Assign'}
          </button>
          <button onClick={onClose} disabled={assigning} className={POPUP_STYLES.button}>
            Cancel
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default AssignCommunicationDialog;
